"use client";

import { useState, useSyncExternalStore } from "react";
import dynamic from "next/dynamic";
import { hyperspeedPresets } from "@/components/react-bits/hyperspeed-preset";
import { ReservationForm } from "@/components/forms/ReservationForm";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { RESERVATION_UI } from "@/components/forms/reservationFormUi";

// Effet WebGL lourd (three.js + postprocessing) chargé uniquement côté client,
// APRÈS le montage, pour ne pas alourdir le first-load JS ni dégrader le LCP.
// Un fond blanc statique reste rendu derrière (le hero n'est jamais vide).
const Hyperspeed = dynamic(() => import("@/components/react-bits/Hyperspeed"), {
  ssr: false,
});

export default function HeroSection({ locale = "fr" }: { locale?: Locale }) {
  const [formStep, setFormStep] = useState<1 | 2>(1);
  // Gate de montage SSG-safe sans setState-in-effect (cf. règle
  // react-hooks/set-state-in-effect) : false au SSR + 1er rendu, true ensuite.
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
  const t = tr(RESERVATION_UI, locale).hero;

  return (
    <section className="relative w-full">
      <section
        id="hero-section"
        className={`relative grid pt-16 transition-[min-height] duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
          formStep === 1 ? "min-h-[135vh]" : "min-h-[100vh]"
        }`}
      >
        {/* Sticky background effect (only inside hero) */}
        <div
          className="pointer-events-none sticky top-0 z-[10] h-[100vh] w-full bg-white [grid-area:1/1]"
          aria-hidden="true"
        >
          <div className="absolute inset-0">
            {mounted && <Hyperspeed effectOptions={hyperspeedPresets.one} />}
            <div className="absolute inset-0" aria-hidden="true" />
          </div>
        </div>

        {/* Content overlay */}
        <div className="relative z-20 mx-auto flex w-full max-w-[90rem] flex-1 flex-col px-4 pb-12 pt-24 [grid-area:1/1] sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h1 className="font-space-age text-2xl tracking-[0.25em] text-black sm:text-3xl md:text-4xl lg:text-5xl">
              {t.h1}
            </h1>
            <p className="mx-auto mt-4 max-w-3xl text-base text-black/75 md:text-lg">
              {t.subtitle}
            </p>
          </div>

          {/* Formulaire de Réservation Express */}
          <div className="relative">
            <ReservationForm onStepChange={setFormStep} locale={locale} />

            {/* Bannière de Personnalisation */}
            {/* <div className="relative z-10 mt-6 flex items-center justify-center px-4">
              <Link
                href="/reservation/personnaliser"
                className="group relative flex w-full flex-col gap-4 rounded-xl border border-white/10 bg-black/60 p-4 text-white shadow-[0_10px_40px_rgba(0,0,0,0.5)] backdrop-blur-sm transition-all hover:border-primary/40 sm:w-auto sm:flex-row sm:items-center sm:gap-3 sm:p-6"
                aria-label={t.customizeAria}
              >
                <div className="flex flex-col items-start">
                  <div className="flex flex-row items-center gap-2">
                    <Icon
                      icon="ph:sliders"
                      className="h-5 w-5 text-primary sm:h-6 sm:w-6"
                      aria-hidden="true"
                    />
                    <span className="text-base font-medium text-white sm:text-lg">
                      Besoin de personnaliser votre trajet ?
                    </span>
                  </div>
                  <span className="text-sm text-white/70">
                    Sièges bébé, bagages supplémentaires, arrêts multiples...
                  </span>
                </div>
                <div className="flex items-center gap-1 self-end sm:ms-2 sm:self-auto">
                  <span className="text-sm text-white/80 group-hover:underline">
                    Voir toutes les options
                  </span>
                  <Icon
                    icon="ph:arrow-right"
                    className="h-4 w-4 text-primary transition-transform group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </div>
              </Link>
            </div> */}
          </div>
        </div>
      </section>
    </section>
  );
}
